import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export type SiteLang = 'es' | 'en';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly translate = inject(TranslateService);
  private readonly storageKey = 'pafar.lang';
  readonly supported: readonly SiteLang[] = ['es', 'en'];
  readonly defaultLang: SiteLang = 'es';
  readonly currentLang = signal<SiteLang>('es');

  init(): void {
    this.translate.addLangs([...this.supported]);
    this.translate.setFallbackLang(this.defaultLang);
    this.apply(this.readSaved() ?? this.defaultLang, false);
  }

  is(lang: SiteLang): boolean {
    return this.currentLang() === lang;
  }

  setLanguage(lang: SiteLang): void {
    if (!this.supported.includes(lang) || this.is(lang)) {
      return;
    }
    this.apply(lang, true);
  }

  private apply(lang: SiteLang, persist: boolean): void {
    this.translate.use(lang).subscribe({
      next: () => {
        this.currentLang.set(lang);
        document.documentElement.lang = lang;
        if (persist) {
          try {
            localStorage.setItem(this.storageKey, lang);
          } catch {
            // storage may be blocked
          }
        }
        const title = this.translate.instant('meta.title');
        if (title && title !== 'meta.title') {
          document.title = title;
        }
        const description = this.translate.instant('meta.description');
        if (!description || description === 'meta.description') {
          return;
        }
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('name', 'description');
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', description);
      }
    });
  }

  private readSaved(): SiteLang | null {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (saved === 'es' || saved === 'en') {
        return saved;
      }
    } catch {
      // ignore
    }
    return null;
  }
}
